export type AutomationConditionLogic = 'any' | 'all';
export type AutomationActionFailureMode = 'continue' | 'stop' | 'require_success';
export type AutomationConditionType = 'keyword' | 'ai';
export type AutomationKeywordMatchMode = 'equals' | 'contains' | 'starts_with';
export type AutomationConditionAnswer = 'yes' | 'no';

export type AutomationKeywordCondition = {
  id: string;
  type: 'keyword';
  matchMode: AutomationKeywordMatchMode;
  keywords: string[];
};

export type AutomationAiCondition = {
  id: string;
  type: 'ai';
  question: string;
  expectedAnswer: AutomationConditionAnswer;
  minConfidence: number;
};

export type AutomationCondition = AutomationKeywordCondition | AutomationAiCondition;

export type AutomationConditionRequest = {
  id: string;
  type: AutomationConditionType;
  matchMode?: AutomationKeywordMatchMode | null;
  keywords?: string[] | null;
  question?: string | null;
  expectedAnswer?: AutomationConditionAnswer | null;
  minConfidence?: number | null;
};

export type AutomationConditionRowResult = {
  id: string;
  matched: boolean;
  answer?: string | null;
  confidence?: number | null;
  skipped?: boolean;
};

export type PreviewAutomationConditionMatchRequest = {
  text: string;
  conditionLogic: AutomationConditionLogic;
  conditions: AutomationConditionRequest[];
};

export type PreviewAutomationConditionMatchResponse = {
  matched: boolean;
  results: AutomationConditionRowResult[];
};

export const AUTOMATION_CONDITION_LIMITS = {
  maxConditions: 8,
  maxAiConditions: 3,
  questionMinLength: 4,
  questionMaxLength: 200,
  minConfidence: 1,
  maxConfidence: 10,
  defaultMinConfidence: 7,
} as const;

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `c_${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`;
  }
  return `c_${Math.random().toString(36).slice(2, 12)}`;
};

export const parseKeywords = (value: string): string[] =>
  value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

export const normalizeConditionLogic = (value: unknown): AutomationConditionLogic =>
  String(value ?? '').trim().toLowerCase() === 'all' ? 'all' : 'any';

export const normalizeActionFailureMode = (value: unknown): AutomationActionFailureMode => {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'stop' || normalized === 'stop_on_failure') {
    return 'stop';
  }
  if (normalized === 'require_success' || normalized === 'require_previous_success') {
    return 'require_success';
  }
  return 'continue';
};

export const normalizeKeywordMatchMode = (value: unknown): AutomationKeywordMatchMode => {
  const normalized = String(value ?? '').trim().toLowerCase().replace('-', '_');
  if (normalized === 'contains') {
    return 'contains';
  }
  if (normalized === 'starts_with' || normalized === 'startswith') {
    return 'starts_with';
  }
  return 'equals';
};

export const normalizeExpectedAnswer = (value: unknown): AutomationConditionAnswer => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'no' || normalized === 'false' || normalized === '0' ? 'no' : 'yes';
};

export const emptyKeywordCondition = (
  matchMode: AutomationKeywordMatchMode = 'equals',
  keywords: string[] = ['+'],
): AutomationKeywordCondition => ({
  id: createId(),
  type: 'keyword',
  matchMode,
  keywords,
});

export const emptyAiCondition = (): AutomationAiCondition => ({
  id: createId(),
  type: 'ai',
  question: '',
  expectedAnswer: 'yes',
  minConfidence: AUTOMATION_CONDITION_LIMITS.defaultMinConfidence,
});

export const normalizeAutomationCondition = (value: any): AutomationCondition | null => {
  const type = String(value?.type ?? '').trim().toLowerCase();
  const id = asString(value?.id) || createId();
  if (type === 'ai') {
    const question = asString(value?.question).trim();
    if (!question) {
      return null;
    }
    return {
      id,
      type: 'ai',
      question,
      expectedAnswer: normalizeExpectedAnswer(value?.expectedAnswer),
      minConfidence: Math.min(
        AUTOMATION_CONDITION_LIMITS.maxConfidence,
        Math.max(
          AUTOMATION_CONDITION_LIMITS.minConfidence,
          Number(value?.minConfidence ?? AUTOMATION_CONDITION_LIMITS.defaultMinConfidence),
        ),
      ),
    };
  }

  const keywords = Array.isArray(value?.keywords)
    ? value.keywords.map((item: unknown) => asString(item).trim()).filter(Boolean)
    : [];
  if (keywords.length === 0) {
    return null;
  }

  return {
    id,
    type: 'keyword',
    matchMode: normalizeKeywordMatchMode(value?.matchMode),
    keywords,
  };
};

export const normalizeAutomationConditions = (
  value: unknown,
  fallbackKeywords: string[] = [],
  fallbackMatchMode: AutomationKeywordMatchMode = 'equals',
): AutomationCondition[] => {
  if (Array.isArray(value)) {
    const conditions = value
      .map(normalizeAutomationCondition)
      .filter((item): item is AutomationCondition => item !== null);
    if (conditions.length > 0) {
      return conditions;
    }
  }

  if (fallbackKeywords.length > 0) {
    return [emptyKeywordCondition(fallbackMatchMode, fallbackKeywords)];
  }

  return [emptyKeywordCondition()];
};

export const toAutomationConditionRequest = (condition: AutomationCondition): AutomationConditionRequest =>
  condition.type === 'ai'
    ? {
      id: condition.id,
      type: 'ai',
      question: condition.question.trim(),
      expectedAnswer: condition.expectedAnswer,
      minConfidence: condition.minConfidence,
    }
    : {
      id: condition.id,
      type: 'keyword',
      matchMode: condition.matchMode,
      keywords: condition.keywords,
    };

export const flattenConditionKeywords = (conditions: AutomationCondition[]): string[] =>
  conditions
    .filter((condition): condition is AutomationKeywordCondition => condition.type === 'keyword')
    .flatMap((condition) => condition.keywords)
    .filter(Boolean);

export const firstKeywordMatchMode = (
  conditions: AutomationCondition[],
  fallback: AutomationKeywordMatchMode = 'equals',
): AutomationKeywordMatchMode =>
  conditions.find((condition): condition is AutomationKeywordCondition => condition.type === 'keyword')
    ?.matchMode
  ?? fallback;

export const isValidAutomationCondition = (condition: AutomationCondition): boolean => {
  if (condition.type === 'ai') {
    const question = condition.question.trim();
    return question.length >= AUTOMATION_CONDITION_LIMITS.questionMinLength
      && question.length <= AUTOMATION_CONDITION_LIMITS.questionMaxLength
      && condition.minConfidence >= AUTOMATION_CONDITION_LIMITS.minConfidence
      && condition.minConfidence <= AUTOMATION_CONDITION_LIMITS.maxConfidence;
  }

  return condition.keywords.some((keyword) => keyword.trim().length > 0);
};

export const countAiConditions = (conditions: AutomationCondition[]): number =>
  conditions.filter((condition) => condition.type === 'ai').length;

export const canAddCondition = (conditions: AutomationCondition[]): boolean =>
  conditions.length < AUTOMATION_CONDITION_LIMITS.maxConditions;

export const canAddAiCondition = (conditions: AutomationCondition[]): boolean =>
  canAddCondition(conditions) && countAiConditions(conditions) < AUTOMATION_CONDITION_LIMITS.maxAiConditions;

export const hasValidAutomationConditions = (conditions: AutomationCondition[]): boolean =>
  conditions.some(isValidAutomationCondition);

export const evaluateKeywordConditionLocally = (
  text: string,
  condition: AutomationKeywordCondition,
): boolean => {
  const value = text.trim();
  return condition.keywords.some((keyword) => {
    const needle = keyword.trim();
    if (!needle) {
      return false;
    }
    if (condition.matchMode === 'contains') {
      return value.toLowerCase().includes(needle.toLowerCase());
    }
    if (condition.matchMode === 'starts_with') {
      return value.toLowerCase().startsWith(needle.toLowerCase());
    }
    return value.toLowerCase() === needle.toLowerCase();
  });
};

export const normalizePreviewResult = (value: any): PreviewAutomationConditionMatchResponse => ({
  matched: Boolean(value?.matched),
  results: Array.isArray(value?.results)
    ? value.results.map((item: any) => ({
      id: asString(item?.id),
      matched: Boolean(item?.matched),
      answer: asOptionalString(item?.answer),
      confidence: Number.isFinite(Number(item?.confidence)) ? Number(item.confidence) : null,
      skipped: Boolean(item?.skipped),
    }))
    : [],
});
